import { ICatalog } from "../../catalogs/models/catalogs.models";
import { ICategory } from "../../categories/models/categories.models";
import { ICurrency } from "../../customers/models/customers.models";
import { ILocation, IPayment_term } from "../../customers/models/customers.models";
import { IInventory, IStockByWarehouse,IProductHandlingUnit, IFile, IProductCurrency } from "../../produits/models/products.models";
import { IPriceBook, IPriceBookSimple, IPriceBookViewDTO } from "../../pricebook/models/pricebook.model";
import type { IOrderUser } from '../../orders/models/order.model';
import { IPagination } from "src/app/shared/models/page.model";
import type { ICustomFieldValues } from "src/app/shared/custom-field-display.service";


export interface IAskForQuotation {
  pricelist_id: number;
  is_final: boolean;
  currency_id?: number | null;
  products: IAskForQuotationProduct[];
}

export interface INewQuotation {
  partner_id: number;
  pricelist_id: number;
  is_final: boolean;
  products: IAskForQuotationProduct[];
  currency_id?: number;
  /** Billing company id; always sent alongside `partner_id` (may differ from the location's company). */
  id_company?: number;
  /** True when the order is created via a newly-added location. */
  is_new?: boolean;
  /** Id of a failed AI placeholder order to reuse in place instead of creating a new one. */
  target_id?: number;
}

export interface IAskForQuotationProduct {
  product_id: number;
  name: string;
  discount: number;
  quantity: number;
  sku: string;
  /** Customer's ERP reference. Resolve display via ProductCodeService (WB-7419). */
  erp_id?: string | null;
  total_discount: number;
  stock?: IInventory;
  price_unit: number | null;
  image?: string;
  increment?: number;
  max_qte?: number | null;
  min_qte?: number;
  handling_unit_id?: number;
}

export interface IUpdateQuotationDetails {
    shipping_date?: string;
    /** `null` clears the term (WB-7463: payment terms are removable inline). */
    payment_terms?: number | null;
    comment?: string;
    validity_date?: string;
    partner_id?: number;
    id_company?: number;
    customer_po_number?: string;
    quote_id?: string;
    owner_id?: number;
}

export interface IQuotationProduct {
    product_id: number;
    product_template_id?: number;
    name: string;
    discount: number;
    initial_discount?: number;
    quantity: number;
    initial_quantity?: number;
    sku: string;
    /** Customer's ERP reference. Resolve display via ProductCodeService (WB-7419). */
    erp_id?: string | null;
    stock?: IInventory;
    price_unit: number | null;
    image?: string;
    images?: any[];
    barcode?: number;
    sales_price?: number;
    increment?: number;
    max_qte?: number | null;
    min_qte?: number;
    can_be_sold_out_of_stock?: boolean;
    cost: number;
    checked: boolean;
    brand_id: number;
    categories?: Array<ICategory>;
    final_price: number;
    extra_discount?: number;
    max_extra_discount?: number;
    is_killing_stock?: boolean;
    killing_stock_limit?: number;
    total_open_quantity?: number;
    status?: string;
    brand_name?: string;
    is_owned?: boolean
    stock_by_warehouse?: IStockByWarehouse[];
    reserved_stock?: IReservedStock[];
    stock_reservation?: {
      id?: number;
      state?: string;
      warehouse_id?: number;
      order_id?: number;
      stock_id?: number;
      quantity?: number;
    }[] | undefined;
    tags?: string[];
    description?: string;
    handling_unit_id?: number;
    handling_units?: IProductHandlingUnit[];
    supported_handling_units?: IProductHandlingUnit[];
    quantity_handling_unit?: number | null;
    customer_order_unit?: { quantity: number; unit?: string | null; id?: number } | null;
    order_line_id?: number,
    best_deal?: IPriceBookViewDTO[] | null;
    best_buy_x_get_y?: IPriceBookViewDTO[] | null;
    is_buy_x_get_y_line?: boolean;
    currencies?: IProductCurrency[];
  }

export interface IQuotationKPI {
    total_orders: number;
    total_orders_change: number;
    average_orders: number;
    average_orders_change: number;
    average_response_time: number;
    average_response_time_change: number;
}

export interface IStatusCount {
  cancel: number;
  draft: number;
  requested: number;
  sale: number;
  sent: number;
  to_be_validated: number;
  pending_inventory: number;
  done: number;
  declined: number;
  all: number;
  archived?: number;
}

export interface IDuplicateInfo {
    has_duplicates: boolean;
    orders: {
        id: number;
        name: string;
        status: string;
        external_id?: string;
        partner_name?: string;
        date_order?: string;
        matched_criteria?: string[];
        unmatched_criteria?: string[];
    }[];
}

export interface IQuotation {
    id: number;
    name: string;
    status: string;
    date_order: string;
    validity_date: string;
    nbr_products: number;
    total_skus?: number;
    amount_total: number;
    estimated_margin?: number;
    /**
     * Snapshot of the company's "hide pricing columns" setting at this
     * order's creation time (WB-7024) — independent of the company's
     * *current* setting, so it doesn't change retroactively.
     */
    hide_pricing_columns?: boolean;
    total_qty: number;
    quotes_count?: number;
    is_final?: boolean;
    pricelist_name?: string;
    company_name?: string;
    location_name?: string;
    location_address?: string;
    location_email?: string;
    customer_po_number?: string;
    buyer_po?: string;
    quote_id?: string;
    latest_event_date?: string;
    score?: IQuotationScore;
    final_score?: number;
    channel?: string;
    currency: ICurrency;
    external_id?: string;
    order_creator?: IOrderUser | null;
    owner?: IOrderUser | null;
    is_archived?: boolean;
    archived_at?: string | null;
    ato_processing_status?: 'processing' | 'completed' | 'needs_review' | null;
    ato_email_subject?: string | null;
    processing_started_at?: string | null;
    ato_sender_email?: string | null;
    /**
     * Carries the volta-ai `ReviewReason` code verbatim (e.g. `"PARSE_EMPTY"`) —
     * resolve it through `ReviewReasonPipe`, never render it directly.
     */
    process_error?: string | null;
    operator_notes?: string | null;
    /** Forward-compatible: the code, should the platform ever persist it explicitly. */
    review_reason?: string | null;
    is_read?: boolean;
    is_duplicate_dismissed?: boolean;
    duplicate_info?: IDuplicateInfo;
}

export interface IQuotationScore {
  kill_stock: IkillStock[];
  score_client: number;
  score_RFQ: number;
  score_date: number;
  summary_client?: string[];
  summary_rfq?: string[];
  summary_date?: string[];
}

export interface IkillStock {
  product_id?: number;
  product_sku?: string;
  /** Served alongside product_sku — see WB-7451 (WB-7419). */
  product_erp_id?: string | null;
  product_name?: string;
  product_stock?: number;
  product_image?: string;
  kill_quotes?: IKillStockQuote[];
}

export interface IKillStockQuote {
  id: number;
  name: string;
  quantity: number;
  final_score: number;
  amount_total: number;
}

export interface IQuotationsKPIs {
  kpi: IQuotationKPI;
  status_count: IStatusCount;
  quotations: IQuotation[];
  processing?: IQuotation[];
  processing_count?: IProcessingCount;
}

export interface IProcessingCount {
  processing: number;
  needs_review: number;
}
export interface IQuoteProductData {
  products: IParseProduct[];
  pricelist_id: number;
  quotation_id: number;
  location_id: number;
  pricelist_name?: string;
  source?: string;
  currency?: ICurrency;
  order_deals?: IPriceBookSimple[];
  channel?: string;
  catalog_default_handling_unit?: { id: number; name: string };
}
export interface IOwnershipHistoryEntry {
  changed_by: IOrderUser | null;
  changed_at: string | null;
  previous_owner: IOrderUser | null;
  new_owner: IOrderUser | null;
  note?: string | null;
}

export interface IQuotationDetail {
    id: number;
    name: string;
    is_final: boolean;
    status: string;
    date_order: string;
    payment_status: string;
    payment_terms: IPayment_term;
    comment: string;
    retailer_comment: string;
    shipping_date: string;
    order_status: string;
    validity_date: string;
    pricelist_id: number;
    nbr_products: number;
    total_skus?: number;
    total_order_lines?: number;
    amount_total: number;
    cost_total?: number;
    margin_total?: number;
    /**
     * Sum of price_subtotal for real (non-gift/non-reward) lines priced
     * 'override'/'catalog'/'ai' -- 'none'-priced lines are excluded from the
     * sum entirely (WB-7024). Computed backend-side; use this (not
     * amount_total) for the order "Total" display.
     */
    priced_lines_total?: number;
    /**
     * True when every real order line is priced 'none' (WB-7024) -- the
     * "Total" summary should read "No price" instead of this figure.
     */
    all_lines_no_price?: boolean;
    /**
     * Snapshot of the company's "hide pricing columns" setting at this
     * order's creation time (WB-7024) — independent of the company's
     * *current* setting, so it doesn't change retroactively.
     */
    hide_pricing_columns?: boolean;
    old_total_amount: number;
    total_qty: number;
    lines_metadata?: IPagination;
    invoice_status: string;
    currency: ICurrency;
    products: IParseProduct[];
    parent: IQuotationDetail | null;
    history?: IHistory[];
    location: ILocation;
    /**
     * The actual delivery location (WB-7836) — distinct from `location`
     * (the billing/customer location) whenever "billing different from
     * shipping" is used. Backend-only field until now: fields that need the
     * Shipping block's own identity (e.g. its erp_id) must read this, not
     * `location`, or they silently show the Customer's data instead.
     */
    shipping_location?: ILocation | null;
    channel?: string;
    ai_details?: IQuotationSourceContentAI;
    pricelist_name?: string;
    pricelist_max_extra_discount?: number;
    agent: Agent;
    /** Immutable creator (create_uid). */
    order_creator?: IOrderUser | null;
    owner?: IOrderUser | null;
    ownership_history?: IOwnershipHistoryEntry[];
    order_deals?: IPriceBookSimple[];
    best_deal?: IPriceBookViewDTO[] | null;
    best_free_shipping?: IPriceBookViewDTO | null;
    customer_po_number?: string;
    quote_id?: string;
    show_full_pricing_data?: boolean;
    force_send?: boolean;
    erp_missing_products?: IErpMissingProduct[] | null;
    erp_missing_inventory?: IErpMissingInventory[] | null;
    is_archived?: boolean;
    archived_at?: string | null;
    external_id?: string;
    order_company?: IOrderCompany;
    duplicate_info?: IDuplicateInfo;
    is_duplicate_dismissed?: boolean;
    segregation_info?: ISegregationInfo | null;
    segregation_splits?: ISegregationSplit[];
}

export interface IOrderCompany {
    id: number;
    name: string;
    company_id: string;
    nbr_locations: number;
    main_location_id: number;
}

export interface ISegregationInfo {
    source_id: number;
    source_name: string;
    mechanism_id: number | null;
    mechanism_name: string | null;
    condition_type: 'brand' | 'category' | 'product_label' | 'product_custom_field' | null;
    custom_field_name: string | null;
    split_label: string | null;
    split_key: string | null;
}

export interface ISegregationSplit {
    id: number;
    name: string;
    mechanism_id: number | null;
    mechanism_name: string | null;
    condition_type: 'brand' | 'category' | 'product_label' | 'product_custom_field' | null;
    custom_field_name: string | null;
    split_label: string | null;
    split_key: string | null;
}

export interface ISentToErpRespone {
  erp_missing_products: IErpMissingProduct[] | boolean;
  erp_missing_inventory: IErpMissingInventory[] | boolean;
}

export interface IErpMissingInventory {
  product_id: number;
  product_name: string;
  product_sku: string;
  /** Served alongside product_sku — see WB-7451 (WB-7419). */
  product_erp_id?: string | null;
  order_qty: number;
  erp_qty: number;
  erp_delivery_date: string | null;
  image: string | null;
}

export interface IErpMissingProduct {
  product_id: number;
  /** Was typed `boolean`, which never matched the payload — `erp_helpers.py`
   *  sends `product_tmpl_id.sku`. Corrected to string as part of WB-7419. */
  product_sku: string;
  /** Served alongside product_sku — see WB-7451 (WB-7419). */
  product_erp_id?: string | null;
  product_name: string;
  image: string | null;
}

export interface IHistoryQuotation {
  id: number;
  name: string;
  status: string;
  date_order: string;
  channel: string | null;
  nbr_products: number;
  total_qty: number;
  amount_total: number;
  /** Snapshot of this order's hide_pricing_columns at creation time (WB-7024). */
  hide_pricing_columns?: boolean;
  /** Sum of priced (non-'none') lines only (WB-7024) -- use instead of amount_total when present. */
  priced_lines_total?: number;
  /** True when every real line on this order is priced 'none' (WB-7024). */
  all_lines_no_price?: boolean;
}

export interface IAutomationAction {
  action_type: string;
  description: string;
}

export interface IHistory {
  event: string;
  description: string;
  create_date: string;
  quotation: IHistoryQuotation;
  /** 'user' (default) or 'automation' for workflow-triggered events. */
  source?: string;
  /** Actions executed in this rule run, grouped onto a single automation card. */
  actions?: IAutomationAction[];
  /** The workflow rule that produced an automation event. */
  rule?: { id: number; name: string } | null;
}

export interface IContact {
  email: string;
  phone: number;
}



/**
 * Document Coordinates (WB-4974) — where an extracted field sits on the source document.
 * Tagged union (`kind`) so each substrate can be added without a breaking change; renderers
 * no-op on kinds they don't handle. `box` = PDF/image, `cell` = spreadsheet (WB-7592).
 */
export interface INormalizedBox {
    /** All values are fractions of the page width/height, top-left origin (0–1). */
    x: number;
    y: number;
    w: number;
    h: number;
}

export interface IPageDimensions {
    width: number;
    height: number;
    unit?: 'pt' | 'px' | string;
}

export interface ISourceLocationBox {
    kind: 'box';
    /** page is 1-indexed within the referenced attachment. */
    document_ref: { attachment_id?: string; attachment_index?: number; page: number };
    page_dimensions: IPageDimensions;
    /** v0 fills exactly one box; the array leaves room for wrapped text later. */
    boxes: INormalizedBox[];
    /** Location confidence — NOT the match status (green/orange/red is derived FE-side). */
    confidence?: number;
}

/** A row span in a rendered spreadsheet (WB-7592). All coordinates are ABSOLUTE sheet coordinates. */
export interface ISourceLocationCell {
    kind: 'cell';
    /** File name, not an attachment id — the parsing service hasn't got one. Absent if unknown. */
    document_ref?: { filename?: string };
    /** Sheet tab name. Only matched when the workbook has more than one sheet. */
    sheet?: string;
    /** Convenience mirror of the row in `range`, absolute and 1-indexed. */
    row?: number;
    /** Absolute A1, always a single-row span (`A37:E37`). The source of truth. */
    range: string;
    confidence?: number;
}

/**
 * A verbatim quote anchored in the email body (WB-7625). No pixels and no file: the body IS the
 * document, so there is nothing to reconcile against an attachment. `quote` is the whole logical
 * order line, which regularly spans several physical lines of the body — so it resolves by
 * substring scan + `Range`, never by element equality. See `email-body-anchor.util`.
 */
export interface ISourceLocationTextRange {
    kind: 'text_range';
    /** Always `{ source: 'email_body' }`. Kept for symmetry with the other kinds; nothing reads it. */
    document_ref?: { source?: string };
    /** Canonical: whitespace runs collapsed to one space, then trimmed. */
    quote: string;
    /** 1-indexed hit among NON-overlapping matches of `quote` in the canonicalized body. */
    occurrence?: number;
    confidence?: number;
}

/** Reserve room for the remaining structure-based formats: | ISourceNode. */
export type ISourceLocation = ISourceLocationBox | ISourceLocationCell | ISourceLocationTextRange;

export type SourceLocationField = 'name' | 'code' | 'quantity' | 'unit_price';

/** All addressable keys in a line's location map: the field-level keys + the line-level `row`. */
export type SourceLocationKey = SourceLocationField | 'row';

/**
 * Per-line location map appended to a line item; every entry is optional (not always locatable).
 * `row` is the full-line bounding box and is the v0 PRIORITY (WB-4974); the field-level keys
 * (name / code / quantity / unit_price) are the second tier. When `row` is absent the FE derives a
 * fallback from the union of the present field boxes (see `deriveRowBox`).
 */
export type IFieldLocations = Partial<Record<SourceLocationField, ISourceLocation>> & {
    row?: ISourceLocation;
};

/**
 * Response of `GET quotations/{id}/document_coordinates` (WB-6815). `source_locations` is served by
 * this dedicated endpoint — NOT inline on `ai_details` line items. Coordinates are keyed by line id:
 * matched lines by `order_line_id`, unmatched (AI rows with no SKU match) by `unmatched_line_id`.
 * Lines with no boxes are omitted; arrays are empty when the order has no stored coordinates.
 */
export interface IDocumentCoordinatesLine {
    order_line_id: number;
    source_locations: IFieldLocations;
}
export interface IDocumentCoordinatesUnmatchedLine {
    unmatched_line_id: number;
    source_locations: IFieldLocations;
}
export interface IDocumentCoordinatesResponse {
    /** WB-7542 dropped `attachment_index` server-side — it was an uncomputed literal `0`. */
    document: Record<string, never>;
    lines: IDocumentCoordinatesLine[];
    unmatched_lines: IDocumentCoordinatesUnmatchedLine[];
}

/**
 * Fallback full-row box when the payload carries no explicit `row` location: the union of the present
 * field boxes that sit on the same page. Pure (no Angular) so it is trivially unit-testable.
 *
 * Reference page = the page of the first present field; only field boxes on that page are unioned, so
 * a line whose fields span pages still yields a sane single-page row box. Returns `null` when there is
 * no locatable field box — including a non-raster kind, which has no box to union.
 */
export function deriveRowBox(locations?: IFieldLocations | null): ISourceLocationBox | null {
    if (!locations) {
        return null;
    }
    const fields: SourceLocationField[] = ['name', 'code', 'quantity', 'unit_price'];
    const candidates = fields
        .map((f) => locations[f])
        .filter((loc): loc is ISourceLocationBox => !!loc && loc.kind === 'box' && (loc.boxes?.length ?? 0) > 0);
    if (!candidates.length) {
        return null;
    }
    const ref = candidates[0];
    const refPage = ref.document_ref?.page;
    const onRefPage = candidates.filter((loc) => loc.document_ref?.page === refPage);
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    onRefPage.forEach((loc) => {
        loc.boxes.forEach((b) => {
            minX = Math.min(minX, b.x);
            minY = Math.min(minY, b.y);
            maxX = Math.max(maxX, b.x + b.w);
            maxY = Math.max(maxY, b.y + b.h);
        });
    });
    const unionBox: INormalizedBox = { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
    return {
        kind: 'box',
        document_ref: { ...ref.document_ref },
        page_dimensions: ref.page_dimensions,
        boxes: [unionBox],
        confidence: ref.confidence,
    };
}

/**
 * WB-6815: attach `source_locations` from the document-coordinates endpoint onto the given lines.
 * Matched lines join by `order_line_id`; unmatched by their DB id (`_unmatchedLineId` ?? `id`) — the
 * same ids the backend keys `lines` / `unmatched_lines` by (parse_unmatched_order_line emits
 * `'id': order_line.id`). Mutates the products in place (mirrors the mock injector) and is a no-op when
 * `coords` is null/empty, so it is safe to call repeatedly or before the lines are populated.
 */
export function applyDocumentCoordinatesToLines(
    products: IParseProduct[] | undefined,
    unmatchedProducts: IParseProduct[] | undefined,
    coords: IDocumentCoordinatesResponse | null | undefined,
): void {
    if (!coords) {
        return;
    }
    if (coords.lines?.length) {
        const byOrderLine = new Map(coords.lines.map((l) => [l.order_line_id, l.source_locations]));
        (products || []).forEach((p) => {
            if (p.order_line_id != null && byOrderLine.has(p.order_line_id)) {
                p.source_locations = byOrderLine.get(p.order_line_id);
            }
        });
    }
    if (coords.unmatched_lines?.length) {
        const byUnmatched = new Map(coords.unmatched_lines.map((l) => [l.unmatched_line_id, l.source_locations]));
        (unmatchedProducts || []).forEach((p) => {
            const uid = (p as { _unmatchedLineId?: number })._unmatchedLineId ?? p.id;
            if (uid != null && byUnmatched.has(uid)) {
                p.source_locations = byUnmatched.get(uid);
            }
        });
    }
}

/**
 * Enlarge the (invisible) doc→list hover targets. Each row's box is grown vertically toward the
 * MIDPOINT between it and its neighbour rows, so the strips tile the column — big and easy to hit —
 * while being overlap-free BY CONSTRUCTION: adjacent strips meet exactly at the midpoint and never
 * cross. This is safe at any density: the tighter the lines, the closer the midpoint, so each strip
 * simply grows less (never onto a neighbour). Growth is also capped to `maxExpandInRowHeights` per
 * side so an isolated row doesn't claim half the page.
 *
 * Pure geometry on normalized 0–1 boxes; returns NEW boxes in the SAME order as the input (so callers
 * map 1:1 by index) and never mutates the inputs — important, since the source row box is shared with
 * the (small, unchanged) visible highlight. Only `y`/`h` change; `x`/`w` are preserved. Pass boxes for
 * a SINGLE page (neighbours are only meaningful within a page).
 */
export function expandRowBoxesToMidpoints(boxes: INormalizedBox[], maxExpandInRowHeights = 2): INormalizedBox[] {
    const result = boxes.map((b) => ({ ...b }));
    const order = boxes.map((_, i) => i).sort((a, b) => boxes[a].y - boxes[b].y);
    order.forEach((idx, k) => {
        const b = boxes[idx];
        const top = b.y;
        const bottom = b.y + b.h;
        const cap = b.h * maxExpandInRowHeights;
        // Grow UP: as far as the cap allows, but never past the midpoint to the previous row's bottom.
        let newTop = Math.max(top - cap, 0);
        if (k > 0) {
            const prev = boxes[order[k - 1]];
            newTop = Math.max(newTop, (prev.y + prev.h + top) / 2);
        }
        newTop = Math.min(newTop, top); // only ever expand, never shrink
        // Grow DOWN: symmetric — capped, and never past the midpoint to the next row's top.
        let newBottom = Math.min(bottom + cap, 1);
        if (k < order.length - 1) {
            const next = boxes[order[k + 1]];
            newBottom = Math.min(newBottom, (bottom + next.y) / 2);
        }
        newBottom = Math.max(newBottom, bottom);
        result[idx] = { ...b, y: newTop, h: newBottom - newTop };
    });
    return result;
}

/**
 * WB-7337 (front-end companion): split children created without their own source content (no
 * `message_id` inherited) can't show any documents. This borrows ONLY the attachments from the
 * source/parent order — never subject/from/content/ai_details, which stay whatever `current`
 * already has (or nothing, if `current` is undefined) so nothing from the source order is ever
 * misattributed to the split child. Pure — safe to call with any `current`/`attachments` shape.
 */
export function withFallbackAttachments(
    current: IQuotationSourceContent | undefined,
    attachments: string[],
): IQuotationSourceContent {
    return {
        ...(current ?? {} as IQuotationSourceContent),
        email: {
            ...(current?.email ?? {} as IQuotationSourceContentEmail),
            attachments,
        },
    };
}

export interface IParseProduct {
    id: number;
    product_id?: number;
    product_template_id?: number;
    name: string;
    raw_phrase?: string;
    notes?: string | null;
    images?: any[];
    quantity: number;
    sales_price?: number;
    sku: string;
    /**
     * Customer's ERP reference for the line's product (WB-7419). Served by
     * `parsers.py` alongside `sku` — see WB-7451. Resolve display through
     * ProductCodeService so the SKU fallback applies; never read directly.
     */
    erp_id?: string | null;
    barcode?: number;
    discount: number;
    cost?: number;
    status?: string;
    price_unit: number;
    final_price?: number | null;
    ai_price?: number | null;
    price_type?: PriceSourceType;
    catalog_price?: number | null;
    image?: string;
    increment?: number;
    max_qte?: number | null;
    min_qte?: number;
    total: number;
    is_owned?: boolean;
    brand_name?: string;
    tags?: string[];
    description?: string;
    stock?: number;
    matched?: boolean;
    added_manually?: boolean;
    brand_id?: number;
    category_id?: number;
    categories?: Array<ICategory>;
    commission_amount?: number;
    extra_discount?: number;
    reserved_stock?: IReservedStock[];
    stock_by_warehouse?: IStockByWarehouse[];
    stock_reservation?: {
      id?: number;
      state?: string;
      warehouse_id?: number;
      order_id?: number;
      stock_id?: number;
      quantity?: number;
    }[] | undefined;
    duplicated?: boolean;
    order_line_id?: number;
    handling_unit_id?: number;
    handling_units?: IProductHandlingUnit[];
    quantity_handling_unit?: number | null;
    quantity_preferred_selling_unit?: number | null;
    quantity_preferred_selling_unit_name?: string | null;
    customer_order_unit?: { quantity: number; unit?: string | null; id?: number } | null;
    handling_unit_raw_phrase?: string;
    handling_unit_raw_quantity?: number;
    best_deal?: IPriceBookViewDTO[] | null;
    best_buy_x_get_y?: IPriceBookViewDTO[] | null;
    is_buy_x_get_y_line?: boolean;
    old_final_price?: number | null;
    old_total_amount?: number | null;
    quantity_for_deal?: number | null;
    amount_for_deal?: number | null;
    has_alternatives?: boolean;
    /** Marks the pre-correction original product among the alternatives returned by getOrderLineAlternatives. */
    is_original?: boolean;
    has_multi_warehouse?: boolean;
    currencies?: IProductCurrency[];
    delivery_date?: string;
    is_gift?: boolean;
    order_line_alerts?: IOrderLineAlert[];
    ai_extracted_name?: string | null;
    ai_extracted_codes?: string | null;
    ai_extracted_unit?: string | null;
    /** Raw AI-extracted quantity, before the WB-7249 sub-unit conversion (Rule 1). */
    ai_extracted_quantity?: number | null;
    /** Raw AI-extracted unit price, before the WB-7249 sub-unit conversion (Rule 2). Equal to ai_price when no conversion applied. */
    ai_extracted_price?: number | null;
    /** True when the AI sub-unit conversion (needs_unit_conversion + conversion_coefficient) actually fired for this line. */
    ai_unit_conversion_applied?: boolean;
    ai_match_method?: string | null;
    ai_match_value?: string | null;
    ai_match_field?: string | null;
    can_be_sold_out_of_stock?: boolean;
    buy_x_get_y_discount?: number;
    sequence?: number;
    /** WB-7399: raw file name this line was parsed from. Null on non-AI/legacy orders; may be `"email_body"`. */
    source?: string | null;
    matched_from_unmatched_line_id?: number;
    /** Document Coordinates (WB-4974): per-field source-document locations. Additive/optional. */
    source_locations?: IFieldLocations;
    /**
     * Product-level (`inventory`) custom field values, keyed by the
     * definition's technical_name (WB-7619). Served by WB-7644 — until that
     * lands the key is absent and the optional columns render empty.
     */
    custom_fields?: ICustomFieldValues;
    /** Side-by-side panel sidecar fields — not from API */
    _correctionIntent?: CorrectionIntent;
    _amtwCode?: string | null;
    _matchedAsNew?: boolean;
    _unmatchedLineId?: number;
}

export interface IUpdateLinesDeltaSummary {
    nbr_products: number;
    total_skus: number;
    total_order_lines: number;
    total_qty: number;
    amount_total: number;
    cost_total?: number;
    margin_total?: number;
    lines_metadata: IPagination;
    /** See IQuotationDetail.priced_lines_total (WB-7024). */
    priced_lines_total?: number;
    /** See IQuotationDetail.all_lines_no_price (WB-7024). */
    all_lines_no_price?: boolean;
}

export interface IUpdateLinesDelta {
    updated: IParseProduct[];
    added: IParseProduct[];
    removed: number[];
    removed_unmatched: number[];
    summary: IUpdateLinesDeltaSummary;
}

export type PriceSourceType = "ai" | "catalog" | "override" | "none";

export interface IOrderLineAlert {
  id: number;
  type: string;
  state: string;
  level: string;
  message_key: string;
  details?: {
    killing_stock_limit?: number;
    total_open_quantity?: number;
    stock_quantity?: number;
  } | null;
}

export interface IQuotationAlertsResponse {
  alerts_by_line: { [orderLineId: string]: IOrderLineAlert[] };
  has_any_open: boolean;
}

export interface ILineDeal {
  best_deal: any[];
  best_free_shipping: any | null;
  best_buy_x_get_y: any[];
}

export interface IQuotationDealsResponse {
  order_deals: {
    best_deal: any[];
    best_free_shipping: any | null;
    best_buy_x_get_y: any | null;
    order_programs: any[];
  };
  deals_by_line: { [orderLineId: string]: ILineDeal };
}

export interface ILineStockResponse {
  stock_by_warehouse: any[];
  reserved_stock: any[];
  stock_reservation: any[];
}

export interface IReservedStock {
  stock_id: number;
  warehouse_id: number;
  order_id: number;
  name: string;
  quantity: number;
  state: 'reserved' | 'draft' | 'confirmed';
}

export interface IQuotationProductFilter {
  sku: string;
  name: string;
}

/** Company/location scope filter: chip shows `name`, backend filters by `id` (sale.order.partner_id). */
export interface IQuotationLocationFilter {
  id: number;
  name?: string;
}

export interface IQuotationFilter {
  quotationInfoFilter: {
    id_order: string | undefined;
    sales_person: string | undefined;
    status: string | undefined;
    channel: string | undefined;
    external_id: string | undefined;
    order_id: string | undefined;
    buyer_po: string | undefined;
    from_date: string | undefined;
    to_date: string | undefined;
    latest_event_date: string | undefined;
    /** "Contains product(s)" filter: chip shows `name`, backend filters by `sku`. */
    products?: IQuotationProductFilter[];
    /** Show only orders that are duplicates of an earlier order with the same PO. */
    has_duplicates?: boolean;
  };
  extraInfoFilter: {
    total_sku_min: number | undefined;
    total_sku_max: number | undefined;
    total_price_min: number | undefined;
    total_price_max: number | undefined;
  };
  organizeFilter: {
    catalogs: ICatalog[];
    /** Location scope filter: chip shows `name`, backend filters by `id`. */
    locations?: IQuotationLocationFilter[];
  };
  /** Same semantics as orders list: operational owner vs create_uid. */
  ownershipFilter?: {
    my_orders?: boolean;
    owner?: IOrderUser | null;
    creator?: IOrderUser | null;
    /**
     * Team scope: every order owned by any member of ANY of these teams (the union of
     * their members). Backend key `team_ids`.
     *
     * Objects, not bare ids, on purpose. `encodeFilterObject` runs `removeEmptyProperties`
     * (drops falsy scalars) then `deepConvertBooleans` (turns booleans into 1/0); an object
     * survives both untouched, and carrying `name` lets the picker render each chip after a
     * URL round-trip without a second lookup. `organizeFilter.locations` and
     * `organizeFilter.catalogs` already prove an ARRAY of `{id, name}` survives the same
     * encode -> URL -> refill -> parse round-trip intact.
     *
     * Absent (never `[]`) when unset: `refillQuotationFilter` ends in
     * `JSON.parse(JSON.stringify(...))`, which strips `undefined` keys but KEEPS an empty
     * array, and an extra own key breaks the `resets every criterion` toEqual.
     */
    teams?: { id: number; name: string }[];
  };
}

export interface IQuotationFilterRequest {
  filters: { key: string; operator: string; value: any }[];
}

export interface IQuotationSourceContent {
    location: ILocation;
    date_order: string;
    amount_total: number;
    nbr_products: number;
    total_qty: number;
    rfq_score: number;
    ai_details: IQuotationSourceContentAI;
    email: IQuotationSourceContentEmail;
    currency?: ICurrency;
}

export interface IExtractedNote {
    id?: number;
    category: string;
    text: string;
    sequence?: number;
    is_edited?: boolean;
    is_deleted?: boolean;
}

export interface IUpdateAiNotes {
    notes: IExtractedNote[];
}

export interface IQuotationSourceContentAI {
    email_id: string;
    intent: number;
    unmatched_order_lines: IParseProduct[];
    /** AI rows later matched by hand — they keep their own `source`. */
    matched_unmatched_order_lines?: IParseProduct[];
    resume: string;
    overall_confidence: number;
    created_by: string;
    created_at: string;
    shipping_autofill_source?: 'single_location_fallback' | 'multiple_locations' | 'no_locations' | 'main_location_fallback' | 'single_location_soft_match' | null;
    suggested_shipping_location?: { id: number; name: string; shipping_address?: string } | null;
    suggested_shipping_location_dismiss?: boolean;
    extracted_info?: {
        original_sender?: string;
        customer: {
            email: string;
            emails?: { email: string, source: string }[];
            name: string;
            company: string;
            title: string;
            phone: string;
            customer_code: string;
        };
        shipping: {
            raw_address: string;
            billing_address: string;
        };
        delivery: {
            requested_date: string;
            raw_date: string;
            requested_time_slot: string;
            raw_phrase: string;
            notes: string;
            confidence: string;
        };
        notes?: {
          extracted: IExtractedNote[];
          summary: string;
        };
    };
}

export interface IQuotationSourceContentEmail {
    title: string;
    subject: string;
    from: string;
    to: string;
    attachments: string[];
    content: string;
}

export interface IUpdateEmailOrder {
    matches?: {
        ai_order_line_name: string;
        match_with_product_id: number;
    }[];
    unmateched_order_lines_to_remove?: string[];
    intent?: number;
}

export interface Agent {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    commission_total: number; 
}

export interface IQuotationAvailabilityReq{
  delivery_date: string;
  quotation_id: number;
}

export interface checkQuotationAvailabilityResponse{
  missing_products: IErpMissingProduct[] | [];
  insufficient_inventory: IErpMissingInventory[] | [];
  accepted: boolean;
}

export interface IDeleteDuplicateProducts {
  order_line_ids: number[];
}
export interface IPromptToOrder {
  unstructured_data: string;
  attachments: IFile[];
}

/**
 * Always-Match-This-Way signal attached to a correction.
 *   permanent — the admin wants a reusable rule created.
 *   temporary — explicit one-time correction, audit only.
 *   unknown   — default when the toggle was not set.
 */
export type CorrectionIntent = 'permanent' | 'temporary' | 'unknown';

export interface IMatchUnmatchedItem {
  unmatched_line_id: number;
  product_id: number;
  quantity?: number;
  handling_unit_id?: number;
  quantity_handling_unit?: number;
  final_price?: number;
  price_type?: PriceSourceType;
  delivery_date?: string;
  correction_intent?: CorrectionIntent;
  amtw_code?: string | null;
}

export interface IIgnoreUnmatchedItem {
  unmatched_line_id: number;
  code: string;
  extracted_name?: string;
}

export interface IUpdateQuotationLinesPayload {
  update?: IUpdateQuotationLineUpdate[];
  add?: IUpdateQuotationLineAdd[];
  remove?: number[];
  removeUnmatched?: number[];
  matchUnmatched?: IMatchUnmatchedItem[];
  ignoreUnmatched?: IIgnoreUnmatchedItem[];
  allProductsPriceType?: 'ai_price' | 'catalog_price';
  /** Default applied to every line that does not carry its own value. */
  correction_intent?: CorrectionIntent;
}

export interface IUpdateQuotationLineAdd {
  product_id?: number;
  quantity?: number;
  handling_unit_id?: number;
  quantity_handling_unit?: number;
  delivery_date?: string;
  final_price?: number;
  price_type?: PriceSourceType;
}

export interface IUpdateQuotationLineUpdate extends IUpdateQuotationLineAdd {
  id: number;
  /** User-editable free-text note for this order line. Empty string clears it. */
  notes?: string | null;
  /** Overrides the payload-level correction_intent for this specific line. */
  correction_intent?: CorrectionIntent;
  amtw_code?: string | null;
}
